import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, downloadFile, gbp } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { EMPTY_INVOICE, InvoiceForm } from "./InvoiceForm";
import { extraForForm, extrasTotal, gbp2 } from "./SaleExtras";

const download = (no, fmt) => downloadFile(`/admin/invoices/${no}/${fmt}`, `Cirencester-4x4-Invoice-${no}.${fmt}`).catch((e) => toast.error(apiError(e)));

export const BTN = "border border-forest px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-forest hover:bg-forest hover:text-paper";
const PAYMENT_METHODS = ["Bank transfer", "Card", "Cash", "Cheque", "Finance"];

// Pre-fill an invoice from a saved Vehicle Sale Agreement (Admin → Sales → Create invoice).
const invoiceFromSale = (s) => ({
  ...EMPTY_INVOICE, sale_date: s.sale_date, buyer_name: s.buyer_name, vehicle_id: s.vehicle_id || "", sale_no: s.sale_no,
  price: s.price, extras: (s.extras || []).map(extraForForm),
  buyer_address: [s.buyer_address, s.buyer_postcode].filter(Boolean).join("\n"),
  payment_method: PAYMENT_METHODS.find((m) => m.toLowerCase() === (s.payment_method || "").trim().toLowerCase()) || "Bank transfer",
  vehicle_model: [s.make, s.model, s.derivative].filter(Boolean).join(" "), registration: s.registration || "", vin: s.vin || "", colour: s.colour || "",
  mileage: s.mileage ? `${Number(s.mileage).toLocaleString("en-GB")} miles` : "",
  conditions: `Sold under Vehicle Sale Agreement ${s.sale_no}, whose terms of sale apply.`,
  other_notes: s.deposit ? `Deposit of ${gbp(s.deposit)} received${s.deposit_date ? ` on ${s.deposit_date}` : ""}.` : "",
});

const InvoiceRow = ({ inv, onEdit }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 border-b border-stone py-4" data-testid={`invoice-row-${inv.invoice_no}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{inv.invoice_no} <span className="font-normal text-charcoal/70">· {inv.sale_date}</span></p>
      <p className="text-sm">{inv.buyer_name} · {inv.vehicle_model}{inv.registration ? ` (${inv.registration})` : ""}</p>
      <p className="text-sm font-semibold text-forest" data-testid={`invoice-total-${inv.invoice_no}`}>{gbp2(inv.price + extrasTotal(inv.extras))}{inv.extras?.length ? <span className="font-normal"> · incl. {inv.extras.length} extra{inv.extras.length > 1 ? "s" : ""}</span> : null}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BTN} onClick={() => download(inv.invoice_no, "pdf")} data-testid={`invoice-pdf-${inv.invoice_no}`}>PDF</button>
      <button type="button" className={BTN} onClick={() => download(inv.invoice_no, "docx")} data-testid={`invoice-docx-${inv.invoice_no}`}>Word</button>
      <button type="button" className={BTN} onClick={() => onEdit(inv)} data-testid={`invoice-edit-${inv.invoice_no}`}>Edit</button>
    </div>
  </li>
);

export default function AdminInvoices() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | {initial, invoiceNo}
  const load = useCallback(() => {
    api.get("/admin/invoices").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const location = useLocation();
  const navigate = useNavigate();
  const fromSale = location.state?.fromSale;
  useEffect(() => {
    if (!fromSale) return;
    setEditing({ initial: invoiceFromSale(fromSale), invoiceNo: null });
    navigate(location.pathname, { replace: true, state: null });
  }, [fromSale, navigate, location.pathname]);
  const startNew = () => setEditing({ initial: { ...EMPTY_INVOICE, vat_number: list[0]?.vat_number ?? "" }, invoiceNo: null });
  return (
    <div data-testid="admin-invoices">
      <AdminTitle title="Invoices">
        {!editing && <button type="button" onClick={startNew} className="btn btn-primary !py-3" data-testid="invoice-new">New invoice</button>}
      </AdminTitle>
      <p className="mb-6 text-sm text-charcoal/75">Sales invoices are private: they contain customer details and are only available to signed-in admins. Numbers are assigned automatically (C4X4-year-101 onwards).</p>
      {editing && (
        <div className="mb-8">
          <InvoiceForm key={editing.invoiceNo || "new"} initial={editing.initial} invoiceNo={editing.invoiceNo}
            onSaved={(inv) => { setEditing(null); load(); download(inv.invoice_no, "pdf"); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <section className="border border-stone bg-paper px-6 py-2">
        {list.length ? <ul>{list.map((inv) => <InvoiceRow key={inv.invoice_no} inv={inv} onEdit={(i) => { setEditing({ initial: { ...EMPTY_INVOICE, ...i, extras: (i.extras || []).map(extraForForm) }, invoiceNo: i.invoice_no }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}</ul>
          : <p className="py-6 text-sm text-charcoal/70" data-testid="invoices-empty">No invoices yet.</p>}
      </section>
    </div>
  );
}
